import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {snakeCase} from 'change-case';
import {useTranslate} from '@computerrock/formation-i18n';
import {withRouter, useHistory} from '@computerrock/formation-router';
import {europeanCountries, alfClientTypes, alfAccountTypes, alfInvoiceTypes, alfAddressTypes, /* alfBusinessRelationTypes, */ persistenceStates} from '@ace-de/eua-entity-types';
import {NoResultsBlock, ToggleTab, ToggleTabSwitch, useStyles, Paginator, HighlightCircle, LoaderSpinner} from '@ace-de/ui-components';
import {Button, ButtonIcon, ButtonPrimary} from '@ace-de/ui-components/buttons';
import {Form, InputField, SelectField, Option} from '@ace-de/ui-components/form';
import {Table, TableCaption, TableHead, TableBody, TableRow, TableCell} from '@ace-de/ui-components/data-elements';
import {infoAlertIcon, Icon, closeIcon, plusIcon, searchIcon, checkmarkIcon, editIcon} from '@ace-de/ui-components/icons';
import * as invoiceActionTypes from '../../invoices/invoiceActionTypes';
import modalIds from '../../modalIds';
import * as contactActionTypes from '../contactActionTypes';
import config from '../../config';

// todo uncomment businessRelations filter related code, once that filtering is implemented on NAV

const initialInvoiceContactSearchParams = {
    client: alfClientTypes.ACE,
    ids: '',
    name: '',
    street: '',
    postCode: '',
    city: '',
    addressTypes: [],
    /* businessRelations: [], */
};

const getValuesFromQueryParams = (initialParams, queryParams) => {
    const params = {...initialParams};
    Object.keys(initialParams).forEach(key => {
        if (!(queryParams.get(key) || (key === 'ids' && queryParams.get('id')))) return;
        params[key] = key === 'ids' ? queryParams.get('ids') || queryParams.get('id') : queryParams.get(key);
    });
    return params;
};

const InvoiceContactSearch = props => {
    const {cx} = useStyles();
    const history = useHistory();
    const {createTranslateShorthand, translate} = useTranslate();
    const translateModal = createTranslateShorthand('invoice_contact_search_modal');
    const {invoiceContactsSearchResults, initiateInvoiceContactCreation, invoice, searchPersistenceState} = props;
    const {isInvoiceContactsSearchError, isInvoiceContactSearchPerformed, initiateInvoiceAccountPartyCreation} = props;
    const {declineInvoiceContactSelection, searchInvoiceContacts, initiateInvoiceContactSelection} = props;
    const {invoiceContactsSearchResultsCount, initiateEditContact, bcErrorDescription} = props;

    const [selectedContact, setSelectedContact] = useState(null);
    const [queryParams, setQueryParams] = useState(new URLSearchParams(history.location.search));
    const [formData, setFormData] = useState(getValuesFromQueryParams(initialInvoiceContactSearchParams, queryParams));
    const [savedFormDataForSearchResults, setSavedFormDataForSearchResults] = useState(formData);

    const isVKRInvoice = invoice?.type === alfInvoiceTypes.VKR;
    const paginatorCount = Math.ceil(invoiceContactsSearchResultsCount / config.DEFAULT_PAGE_SIZE);
    const isSearchInProgress = searchPersistenceState === persistenceStates.PENDING;

    const formatQueryParams = formData => {
        if (!formData) return;
        const searchQueryParams = new URLSearchParams();

        Object.keys(formData).forEach(formField => {
            if (formData[formField] !== undefined && formData[formField] !== '') {
                if (formField !== 'addressTypes' /* && formField !== 'businessRelations' */) {
                    searchQueryParams.append(`${formField}`, formData[formField]);
                }

                if ((formField === 'addressTypes' /* || formField === 'businessRelations' */)
                    && Array.isArray(formData[formField]) && formData[formField].length > 0) {
                    formData[formField].forEach(formFieldValue => {
                        if (formFieldValue !== '') searchQueryParams.append(formField, formFieldValue);
                    });
                }
            }
        });

        return searchQueryParams;
    };

    const handleOnChange = (key, value) => {
        setFormData(prevState => ({
            ...prevState,
            [key]: value,
        }));
    };

    const handleOnToggleSwitch = value => {
        handleOnChange('client', value);
        let isFormEmpty = true;
        Object.keys(savedFormDataForSearchResults).forEach(formField => {
            if (formField !== 'client' && !!savedFormDataForSearchResults[formField]) isFormEmpty = false;
        });
        if (isFormEmpty) return;

        const searchQueryParams = formatQueryParams({
            ...savedFormDataForSearchResults,
            client: value,
            modal: modalIds.BC_CONTACT_MODAL,
        });

        searchInvoiceContacts({searchQueryParams});

        setSelectedContact(null);
    };

    const handleOnSubmit = () => {
        const searchQueryParams = formatQueryParams({
            ...formData,
            modal: modalIds.BC_CONTACT_MODAL,
        });
        if (searchQueryParams.size === 2 && searchQueryParams.has('modal') && searchQueryParams.has('client')) return;

        searchInvoiceContacts({searchQueryParams});
        setSelectedContact(null);
        setSavedFormDataForSearchResults(formData);
    };

    const handleContactSelection = () => {
        initiateInvoiceContactSelection({selectedContact, isVKRInvoice});
    };

    const handleContactCreation = async () => {
        await declineInvoiceContactSelection();
        initiateInvoiceContactCreation({
            client: formData.client,
            invoiceId: invoice.id,
        });
    };

    const handleAccountPartyCreation = async selectedContact => {
        await declineInvoiceContactSelection();
        initiateInvoiceAccountPartyCreation({
            client: formData.client,
            accountPartyType: isVKRInvoice ? alfAccountTypes.DEBTOR : alfAccountTypes.CREDITOR,
            selectedContact,
            invoiceId: invoice.id,
        });
    };

    const handleEditContact = async selectedContact => {
        await declineInvoiceContactSelection();
        initiateEditContact({
            client: formData.client,
            selectedContact,
            invoiceId: invoice.id,
        });
    };

    const handlePaginationPage = page => {
        const searchQueryParams = formatQueryParams({
            ...formData,
            modal: modalIds.BC_CONTACT_MODAL,
        });
        searchQueryParams.set('page', `${page}`);
        searchQueryParams.set('size', `${config.DEFAULT_PAGE_SIZE}`);

        searchInvoiceContacts({searchQueryParams});
        setSelectedContact(null);
        setQueryParams(searchQueryParams);
    };

    const hasAccountPartyId = selectedContact && !!(isVKRInvoice
        ? selectedContact.debtorId
        : selectedContact.creditorId
    );

    return (
        <div>
            <div
                className={cx([
                    'global!ace-u-padding--32-32-0',
                    'global!ace-u-width--full',
                    'global!ace-u-flex',
                    'global!ace-u-flex--justify-space-between',
                    'global!ace-u-flex--align-center',
                ])}
            >
                <ToggleTabSwitch
                    name="clientBCSearch"
                    className={cx('global!ace-u-margin--bottom-24')}
                    onChange={value => handleOnToggleSwitch(value)}
                    value={formData.client}
                    isDisabled={isSearchInProgress}
                >
                    {Object.values(alfClientTypes).map(tab => (
                        <ToggleTab
                            key={tab}
                            name={`${tab.toLowerCase()}Tab`}
                            value={tab}
                        >
                            {tab}
                        </ToggleTab>
                    ))}
                </ToggleTabSwitch>
                <Button onClick={handleContactCreation} isDisabled={isSearchInProgress}>
                    <Icon
                        className={cx([
                            'global!ace-c-icon--color-highlight',
                            'global!ace-c-icon--s',
                            'global!ace-u-margin--right-8',
                        ])}
                        icon={plusIcon}
                    />
                    {translateModal('button.create_contact')}
                </Button>
            </div>
            <div className={cx(['global!ace-u-padding--0-32', 'global!ace-u-width--full'])}>
                <Form name="invoiceContactSearch" onSubmit={handleOnSubmit}>
                    <p
                        className={cx([
                            'global!ace-u-margin--24-0',
                            'global!ace-u-typography--variant-body-bold',
                        ])}
                    >
                        {translateModal('form_title.contact_data')}
                    </p>
                    <div
                        className={cx([
                            'global!ace-u-margin--bottom-40',
                            'global!ace-u-width--full',
                            'global!ace-u-grid',
                            'global!ace-u-flex--align-flex-end',
                            'global!ace-u-flex--justify-space-between',
                        ])}
                        onKeyDown={event => {
                            if (event.key === 'Enter') handleOnSubmit();
                        }}
                    >
                        {Object
                            .keys(formData)
                            .filter(formField => !['addressTypes'/* , 'businessRelations' */].includes(formField))
                            .map(formField => {
                                if (formField === 'client') {
                                    return null;
                                }

                                return (
                                    <div
                                        key={formField}
                                        className={cx({
                                            'global!ace-u-grid-column--span-1': ['ids', 'postCode'].includes(formField),
                                            'global!ace-u-grid-column--span-2': !['ids', 'postCode'].includes(formField),
                                        })}
                                    >
                                        <InputField
                                            name={formField}
                                            label={translateModal(`input_label.${formField}`)}
                                            value={formData[formField]}
                                            className={cx([
                                                'global!ace-u-width--full',
                                            ])}
                                            onChange={value => handleOnChange(formField, value)}
                                            isDisabled={isSearchInProgress}
                                        />
                                    </div>
                                );
                            })}
                        <div
                            key="addressTypes"
                            className={cx(['global!ace-u-grid-column--span-3'])}
                        >
                            <SelectField
                                name="addressTypes"
                                label={translateModal('input_label.address_type')}
                                value={formData.addressTypes}
                                isMultipleChoice={true}
                                className={cx('global!ace-u-width--full')}
                                onChange={value => handleOnChange('addressTypes', value)}
                                isDisabled={isSearchInProgress}
                            >
                                {Object.keys(alfAddressTypes).map(alfAddressType => (
                                    <Option
                                        name={`${alfAddressType.toLowerCase()}Option`}
                                        key={alfAddressType}
                                        value={alfAddressType}
                                    >
                                        {translate(`global.contact_address_type.${alfAddressType.toLowerCase()}`)}
                                    </Option>
                                ))}
                            </SelectField>
                        </div>
                        {/* <div
                            key="businessRelations"
                            className={cx(['global!ace-u-grid-column--span-2'])}
                        >
                            <SelectField
                                name="types"
                                label={translateModal('input_label.business_relations')}
                                value={formData.businessRelations}
                                isMultipleChoice={true}
                                className={cx('global!ace-u-width--full')}
                                onChange={value => handleOnChange('businessRelations', value)}
                            >
                                {Object.values(alfBusinessRelationTypes)
                                    // eslint-disable-next-line max-len
                                    .filter(businessRelationType => (
                                    businessRelationType !== alfBusinessRelationTypes.MEMBER
                                    )).map(businessRelationType => (
                                        <Option
                                            name={`${businessRelationType.toLowerCase()}Option`}
                                            key={businessRelationType}
                                            value={businessRelationType}
                                        >
                                            {translateModal(`select_option.${businessRelationType.toLowerCase()}`)}
                                        </Option>
                                    ))}
                            </SelectField>
                        </div> */}
                        <ButtonIcon
                            name="searchButton"
                            icon={searchIcon}
                            type="submit"
                            className={cx([
                                'global!ace-u-flex--basis-5',
                                'global!ace-u-width--64',
                                'global!ace-u-height--48',
                            ])}
                            isDisabled={isSearchInProgress}
                        />
                    </div>
                </Form>
            </div>
            <div className={cx('global!ace-u-flex', 'global!ace-u-flex--align-center', 'global!ace-u-flex--direction-column')}>
                <Table qaIdent="invoice-contacts-search-results" className={cx('global!ace-u-padding--24')}>
                    {isInvoiceContactSearchPerformed
                    && (isInvoiceContactsSearchError || invoiceContactsSearchResults.length === 0)
                    && !isSearchInProgress
                    && (
                        <TableCaption>
                            <NoResultsBlock
                                colorVariant={!isInvoiceContactsSearchError ? 'primary-highlight' : 'negative'}
                                icon={(
                                    <Icon
                                        className={cx('ace-c-icon--xxl', {
                                            'global!ace-c-icon--color-highlight': !isInvoiceContactsSearchError,
                                            'global!ace-c-icon--color-warning': isInvoiceContactsSearchError,
                                        })}
                                        icon={infoAlertIcon}
                                    />
                                )}
                                description={!isInvoiceContactsSearchError
                                    ? translateModal('no_results.description')
                                    : translateModal('results_error.description')
                                }
                                message={!isInvoiceContactsSearchError
                                    ? translateModal('no_results.message')
                                    : bcErrorDescription || translateModal('results_error.message')
                                }
                            />
                        </TableCaption>
                    )}
                    {isSearchInProgress && (
                        <TableCaption>
                            <HighlightCircle
                                className={cx([
                                    'ace-c-highlight-circle--medium',
                                    'ace-c-highlight-circle--primary-highlight',
                                    'global!ace-u-margin--64-0',
                                ])}
                            >
                                <LoaderSpinner />
                            </HighlightCircle>
                            <p className={cx('global!ace-u-margin--bottom-16', 'global!ace-u-typography--variant-h4')}>
                                {translateModal('loader_message.search_in_progress')}
                            </p>
                        </TableCaption>
                    )}
                    <TableHead>
                        <TableRow>
                            <TableCell
                                colSpan={2}
                                qaIdentPart="invoice-contact-id"
                                className={cx('ace-u-padding--left-32')}
                            >
                                {translateModal('table_data_row.contact_id')}
                            </TableCell>
                            <TableCell colSpan={1} qaIdentPart="invoice-contact-type">
                                {translateModal('table_data_row.type')}
                            </TableCell>
                            <TableCell colSpan={2} qaIdentPart="invoice-contact-business-relations">
                                {translateModal('table_data_row.businessRelation')}
                            </TableCell>
                            <TableCell colSpan={2} qaIdentPart="invoice-contact-name">
                                {translateModal('table_data_row.name')}
                            </TableCell>
                            <TableCell colSpan={2} qaIdentPart="invoice-contact-address-street">
                                {translateModal('table_data_row.address')}
                            </TableCell>
                            <TableCell colSpan={1} qaIdentPart="invoice-contact-address-post-code">
                                {translateModal('table_data_row.postCode')}
                            </TableCell>
                            <TableCell colSpan={2} qaIdentPart="invoice-contact-address-city">
                                {translateModal('table_data_row.city')}
                            </TableCell>
                            <TableCell colSpan={2} qaIdentPart="invoice-contact-address-country">
                                {translateModal('table_data_row.country')}
                            </TableCell>
                            <TableCell colSpan={2} qaIdentPart="invoice-contact-creditor-id">
                                {translateModal('table_data_row.creditor')}
                            </TableCell>
                            <TableCell
                                colSpan={2}
                                qaIdentPart="invoice-contact-debtor-id"
                                className={cx('ace-u-padding--right-32')}
                            >
                                {translateModal('table_data_row.debtor')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!isInvoiceContactsSearchError && invoiceContactsSearchResults.map(contact => (
                            <TableRow
                                key={contact.id}
                                onClick={() => setSelectedContact(contact)}
                                className={cx({
                                    'global!ace-u-typography--variant-body-bold': selectedContact?.id === contact.id,
                                })}
                                qaIdentPart={contact.id}
                            >
                                <TableCell
                                    colSpan={2}
                                    qaIdentPart="invoice-contact-id"
                                    qaIdentPartPostfix={contact.id}
                                    className={cx('ace-u-padding--left-32')}
                                >
                                    {contact.id || '-'}
                                </TableCell>
                                <TableCell colSpan={1} qaIdentPart="invoice-contact-type" qaIdentPartPostfix={contact.id}>
                                    {translateModal(`contact_type.${contact.type.toLowerCase()}`)}
                                </TableCell>
                                <TableCell colSpan={2} qaIdentPart="invoice-contact-business-relations" qaIdentPartPostfix={contact.id}>
                                    {contact.businessRelations.length > 0
                                        ? contact.businessRelations.map((businessRelation, idx) => (
                                            `${translate(`global.business_relation_type.${businessRelation.toLowerCase()}`)}${idx < (contact.businessRelations.length - 1) ? ', ' : ''}`
                                        ))
                                        : '-'}
                                </TableCell>
                                <TableCell colSpan={2} qaIdentPart="invoice-contact-name" qaIdentPartPostfix={contact.id}>
                                    {contact.name || '-'}
                                </TableCell>
                                <TableCell colSpan={2} qaIdentPart="invoice-contact-address-street" qaIdentPartPostfix={contact.id}>
                                    {contact.address?.street || '-'}
                                </TableCell>
                                <TableCell colSpan={1} qaIdentPart="invoice-contact-address-post-code" qaIdentPartPostfix={contact.id}>
                                    {contact.address?.postCode || '-'}
                                </TableCell>
                                <TableCell colSpan={2} qaIdentPart="invoice-contact-address-city" qaIdentPartPostfix={contact.id}>
                                    {contact.address?.city || '-'}
                                </TableCell>
                                <TableCell colSpan={2} qaIdentPart="invoice-contact-address-country" qaIdentPartPostfix={contact.id}>
                                    {contact.address?.country
                                        ? Object.values(europeanCountries).find(country => {
                                            return country?.name === contact.address.country;
                                        }) ? translate(
                                                `global.country.${snakeCase(contact.address.country)}`,
                                            ) : translate('global.country.other')
                                        : '-'}
                                </TableCell>
                                <TableCell colSpan={2} qaIdentPart="invoice-contact-creditor-id" qaIdentPartPostfix={contact.id}>
                                    {contact.creditorId
                                        ? (
                                            <Icon
                                                className={cx('global!ace-c-icon--color-success')}
                                                icon={checkmarkIcon}
                                            />
                                        )
                                        : <Icon icon={closeIcon} />}
                                </TableCell>
                                <TableCell
                                    colSpan={2}
                                    qaIdentPart="invoice-contact-debtor-id"
                                    qaIdentPartPostfix={contact.id}
                                    className={cx('ace-u-padding--right-32')}
                                >
                                    {contact.debtorId
                                        ? (
                                            <Icon
                                                className={cx('global!ace-c-icon--color-success')}
                                                icon={checkmarkIcon}
                                            />
                                        )
                                        : <Icon icon={closeIcon} />}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {invoiceContactsSearchResultsCount > 0 && !isSearchInProgress && (
                    <Paginator
                        page={+queryParams.get('page')}
                        count={paginatorCount}
                        onClick={page => handlePaginationPage(page)}
                    />
                )}
            </div>
            {(!isInvoiceContactSearchPerformed || invoiceContactsSearchResultsCount > 0) && (
                <div
                    className={cx([
                        'global!ace-u-margin--32',
                        'global!ace-u-flex',
                        'global!ace-u-flex--align-center',
                        'global!ace-u-flex--justify-flex-end',
                    ])}
                >
                    <Button isDisabled={!selectedContact} onClick={() => handleEditContact(selectedContact)}>
                        <Icon
                            icon={editIcon}
                            className={cx('global!ace-u-margin--right-8', {
                                'global!ace-c-icon--color-disabled': !selectedContact,
                                'global!ace-c-icon--color-highlight': selectedContact,
                            })}
                        />
                        {translateModal('button_label.edit_contact')}
                    </Button>
                    {!hasAccountPartyId && (
                        <ButtonPrimary
                            isDisabled={!selectedContact}
                            className={cx('global!ace-u-margin--left-24')}
                            onClick={() => handleAccountPartyCreation(selectedContact)}
                        >
                            <Icon
                                icon={plusIcon}
                                className={cx('global!ace-c-icon--color-contrast', 'global!ace-u-margin--right-8')}
                            />
                            {translateModal(`button_label.add_${isVKRInvoice ? 'debtor' : 'creditor'}`)}
                        </ButtonPrimary>
                    )}
                    {selectedContact && hasAccountPartyId && (
                        <ButtonPrimary
                            isDisabled={!selectedContact}
                            className={cx('global!ace-u-margin--left-24')}
                            onClick={handleContactSelection}
                        >
                            <Icon
                                icon={plusIcon}
                                className={cx('global!ace-c-icon--color-contrast', 'global!ace-u-margin--right-8')}
                            />
                            {translateModal(`button_label.select_${isVKRInvoice ? 'debtor' : 'creditor'}`)}
                        </ButtonPrimary>
                    )}
                </div>
            )}
        </div>
    );
};

InvoiceContactSearch.propTypes = {
    invoice: PropTypes.object,
    declineInvoiceContactSelection: PropTypes.func.isRequired,
    searchInvoiceContacts: PropTypes.func.isRequired,
    initiateInvoiceContactSelection: PropTypes.func.isRequired,
    invoiceContactsSearchResults: PropTypes.array,
    isInvoiceContactsSearchError: PropTypes.bool,
    isInvoiceContactSearchPerformed: PropTypes.bool,
    initiateInvoiceContactCreation: PropTypes.func.isRequired,
    initiateInvoiceAccountPartyCreation: PropTypes.func.isRequired,
    initiateEditContact: PropTypes.func.isRequired,
    invoiceContactsSearchResultsCount: PropTypes.number,
    searchPersistenceState: PropTypes.string.isRequired,
    bcErrorDescription: PropTypes.string,
};

InvoiceContactSearch.defaultProps = {
    invoice: null,
    invoiceContactsSearchResults: [],
    isInvoiceContactsSearchError: false,
    isInvoiceContactSearchPerformed: false,
    invoiceContactsSearchResultsCount: 0,
    bcErrorDescription: '',
};

const mapStateToProps = state => {
    return {
        invoiceContactsSearchResults: state.contacts.contactsSearchResults,
        invoiceContactsSearchResultsCount: state.contacts.contactsSearchResultsCount,
        isInvoiceContactsSearchError: state.contacts.isContactsSearchError,
        isInvoiceContactSearchPerformed: state.contacts.isContactSearchPerformed,
        searchPersistenceState: state.contacts.searchPersistenceState,
        bcErrorDescription: state.contacts.errorDescription,
    };
};

const mapDispatchToProps = dispatch => ({
    declineInvoiceContactSelection: () => dispatch({
        type: invoiceActionTypes.DECLINE_INVOICE_CONTACT_SEARCH,
    }),
    searchInvoiceContacts: payload => dispatch({
        type: contactActionTypes.SEARCH_CONTACTS,
        payload,
    }),
    initiateInvoiceContactSelection: payload => dispatch({
        type: invoiceActionTypes.SELECT_INVOICE_CONTACT,
        payload,
    }),
    initiateInvoiceContactCreation: payload => dispatch({
        type: contactActionTypes.INITIATE_CREATE_CONTACT_MODAL_FLOW,
        payload,
    }),
    initiateInvoiceAccountPartyCreation: payload => dispatch({
        type: contactActionTypes.INITIATE_CREATE_ACCOUNT_PARTY_MODAL_FLOW,
        payload,
    }),
    initiateEditContact: payload => dispatch({
        type: contactActionTypes.INITIATE_EDIT_CONTACT_MODAL_FLOW,
        payload,
    }),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(InvoiceContactSearch));
